import type { MessageKey } from "@expertloop/i18n";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../services/api";
import type { Report, ReportStatus, ReportReason } from "@expertloop/shared";
import { useI18n } from "@expertloop/i18n/react";

interface EnrichedReport extends Report {
	reporter: {
		id?: string;
		email: string;
		firstName?: string;
		lastName?: string;
		role?: string;
	};
	reported: {
		id?: string;
		email?: string;
		firstName?: string;
		lastName?: string;
		role?: string;
	};
}

interface ReportsResponse {
	reports: EnrichedReport[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

interface ReportStats {
	pending: number;
	reviewed: number;
	resolved: number;
	dismissed: number;
	total: number;
}

const REASON_LABEL_KEYS: Record<ReportReason, MessageKey> = {
	harassment: "reportModal.harassment",
	spam: "reportModal.spam",
	inappropriate_content: "reportModal.inappropriate",
	fraud: "reportModal.fraud",
	unprofessional_behavior: "reportModal.unprofessional",
	other: "reportModal.other",
};

const REASON_ICONS: Record<ReportReason, string> = {
	harassment: "fa-user-slash",
	spam: "fa-comment-slash",
	inappropriate_content: "fa-ban",
	fraud: "fa-exclamation-triangle",
	unprofessional_behavior: "fa-thumbs-down",
	other: "fa-flag",
};

export function ReportsPage() {
	const { t } = useI18n();
	const [reports, setReports] = useState<EnrichedReport[]>([]);
	const [stats, setStats] = useState<ReportStats | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [statusFilter, setStatusFilter] = useState<ReportStatus | "all">("all");
	const [updatingId, setUpdatingId] = useState<string | null>(null);
	const [expandedId, setExpandedId] = useState<string | null>(null);

	useEffect(() => {
		const fetchReports = async () => {
			setLoading(true);
			try {
				const [reportsData, statsData] = await Promise.all([
					adminApi.get<ReportsResponse>(
						`/admin/reports?page=${page}&pageSize=20&status=${statusFilter}`,
					),
					adminApi.get<{ stats: ReportStats }>("/admin/reports/stats"),
				]);
				setReports(reportsData.reports);
				setTotalPages(reportsData.totalPages);
				setStats(statsData.stats);
			} catch (err) {
				setError(err instanceof Error ? err.message : t("adminReports.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchReports();
	}, [page, statusFilter]);

	const updateStatus = async (reportId: string, status: ReportStatus) => {
		setUpdatingId(reportId);
		try {
			await adminApi.patch(`/admin/reports/${reportId}`, { status });
			setReports((prev) =>
				prev.map((r) => (r.id === reportId ? { ...r, status } : r))
			);
			const statsData = await adminApi.get<{ stats: ReportStats }>("/admin/reports/stats");
			setStats(statsData.stats);
		} catch (err) {
			setError(err instanceof Error ? err.message : t("adminReports.updateFailed"));
		} finally {
			setUpdatingId(null);
		}
	};

	const formatDate = (date: Date | string) => {
		return new Date(date).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getDisplayName = (user: { firstName?: string; lastName?: string; email?: string }) => {
		if (user.firstName && user.lastName) {
			return `${user.firstName} ${user.lastName}`;
		}
		return user.email || t("adminReports.unknownUser");
	};

	const getStatusStyle = (status: ReportStatus) => {
		switch (status) {
			case "pending":
				return { background: "#fef3c7", color: "#92400e" };
			case "reviewed":
				return { background: "#dbeafe", color: "#1e40af" };
			case "resolved":
				return { background: "#dcfce7", color: "#166534" };
			case "dismissed":
				return { background: "#f3f4f6", color: "#6b7280" };
		}
	};

	if (loading && reports.length === 0) {
		return <div className="loading">{t('adminReports.loading')}</div>;
	}

	return (
		<div className="reports-page">
			<div className="page-header">
				<h1>{t('adminReports.title')}</h1>
			</div>

			{/* Stats Summary */}
			{stats && (
				<div className="reports-stats">
					<button
						className={`report-stat-card ${statusFilter === "all" ? "active" : ""}`}
						onClick={() => { setStatusFilter("all"); setPage(1); }}
					>
						<div className="stat-number">{stats.total}</div>
						<div className="stat-label">{t('adminReports.total')}</div>
					</button>
					<button
						className={`report-stat-card pending ${statusFilter === "pending" ? "active" : ""}`}
						onClick={() => { setStatusFilter("pending"); setPage(1); }}
					>
						<div className="stat-number">{stats.pending}</div>
						{/* `chatsPage.filter.pending` n'existe plus au catalogue (défaut
						    préexistant) : le libellé vit désormais avec les statuts admin */}
						<div className="stat-label">{t('adminChatsPage.pending')}</div>
					</button>
					<button
						className={`report-stat-card reviewed ${statusFilter === "reviewed" ? "active" : ""}`}
						onClick={() => { setStatusFilter("reviewed"); setPage(1); }}
					>
						<div className="stat-number">{stats.reviewed}</div>
						<div className="stat-label">{t('adminReports.reviewed')}</div>
					</button>
					<button
						className={`report-stat-card resolved ${statusFilter === "resolved" ? "active" : ""}`}
						onClick={() => { setStatusFilter("resolved"); setPage(1); }}
					>
						<div className="stat-number">{stats.resolved}</div>
						<div className="stat-label">{t('adminReports.resolved')}</div>
					</button>
					<button
						className={`report-stat-card dismissed ${statusFilter === "dismissed" ? "active" : ""}`}
						onClick={() => { setStatusFilter("dismissed"); setPage(1); }}
					>
						<div className="stat-number">{stats.dismissed}</div>
						<div className="stat-label">{t('adminReports.dismissed')}</div>
					</button>
				</div>
			)}

			{error && <div className="error-message">{error}</div>}

			{/* Reports List */}
			{reports.length === 0 ? (
				<div className="card">
					<div className="empty-state">
						<i className="fas fa-flag" style={{ fontSize: "2rem", color: "var(--color-gray-400)", marginBottom: "1rem" }}></i>
						<h3>{t('adminReports.emptyTitle')}</h3>
						<p>
							{statusFilter === "all"
								? t('adminReports.emptyAll')
								: t('adminReports.emptyFiltered', { status: statusFilter })}
						</p>
					</div>
				</div>
			) : (
				<div className="reports-list">
					{reports.map((report) => (
						<div key={report.id} className={`report-card ${report.status}`}>
							<div className="report-card-header">
								<div className="report-reason">
									<i className={`fas ${REASON_ICONS[report.reason]}`}></i>
									<span>{t(REASON_LABEL_KEYS[report.reason])}</span>
								</div>
								<span className="report-status" style={getStatusStyle(report.status)}>
									{report.status}
								</span>
							</div>

							<div className="report-parties">
								<div className="report-party">
									<div className="party-label">{t('adminReports.reportedBy')}</div>
									<div className="party-name">{getDisplayName(report.reporter)}</div>
									<div className="party-email">{report.reporter.email}</div>
									{report.reporter.role && (
										<span className="party-role">{report.reporter.role}</span>
									)}
								</div>
								<div className="report-arrow">
									<i className="fas fa-arrow-right"></i>
								</div>
								<div className="report-party">
									<div className="party-label">{t('adminReports.reportedUser')}</div>
									<div className="party-name">{getDisplayName(report.reported)}</div>
									<div className="party-email">{report.reported.email || report.reportedUserEmail}</div>
									{report.reported.role && (
										<span className="party-role">{report.reported.role}</span>
									)}
								</div>
							</div>

							<div className="report-description">
								<button
									className="description-toggle"
									onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}
								>
									<i className={`fas fa-chevron-${expandedId === report.id ? "up" : "down"}`}></i>
									{expandedId === report.id ? t('adminReports.hideDetails') : t('adminReports.showDetails')}
								</button>
								{expandedId === report.id && (
									<div className="description-content">
										<p>{report.description || t('adminReports.noDescription')}</p>
										{report.resolution && (
											<div className="resolution-note">
												<strong>{t('adminReports.resolutionLabel')}</strong> {report.resolution}
											</div>
										)}
									</div>
								)}
							</div>

							<div className="report-footer">
								<div className="report-meta">
									<span className="report-date">
										<i className="fas fa-clock"></i>
										{formatDate(report.createdAt)}
									</span>
									{report.chatId && (
										<Link to={`/chats/${report.chatId}`} className="report-chat-link">
											<i className="fas fa-comments"></i>
											{t('adminReports.viewChat')}
										</Link>
									)}
								</div>

								{(report.status === "pending" || report.status === "reviewed") && (
									<div className="report-actions">
										{report.status === "pending" && (
											<button
												className="btn btn-sm"
												onClick={() => updateStatus(report.id, "reviewed")}
												disabled={updatingId === report.id}
											>
												<i className="fas fa-eye"></i>
												{t('adminReports.markReviewed')}
											</button>
										)}
										<button
											className="btn btn-sm btn-success"
											onClick={() => updateStatus(report.id, "resolved")}
											disabled={updatingId === report.id}
										>
											<i className="fas fa-check"></i>
											{t('adminReports.resolve')}
										</button>
										<button
											className="btn btn-sm btn-secondary"
											onClick={() => updateStatus(report.id, "dismissed")}
											disabled={updatingId === report.id}
										>
											<i className="fas fa-times"></i>
											{t('adminReports.dismiss')}
										</button>
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{totalPages > 1 && (
				<div className="pagination">
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						{t('common.previous')}
					</button>
					<span className="pagination-info">
						{t('common.pageOf', { page, totalPages })}
					</span>
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={page === totalPages}
					>
						{t('common.next')}
					</button>
				</div>
			)}

			<style>{`
				.reports-stats {
					display: flex;
					gap: 0.75rem;
					margin-bottom: 1.5rem;
					flex-wrap: wrap;
				}

				.report-stat-card {
					flex: 1;
					min-width: 100px;
					background: white;
					border: 2px solid var(--color-gray-200);
					border-radius: 0.75rem;
					padding: 1rem;
					text-align: center;
					cursor: pointer;
					transition: all 0.15s;
				}

				.report-stat-card:hover {
					border-color: var(--color-gray-300);
					background: var(--color-gray-50);
				}

				.report-stat-card.active {
					border-color: var(--color-primary);
					background: #eef2ff;
				}

				.report-stat-card.pending { border-left: 4px solid #f59e0b; }
				.report-stat-card.reviewed { border-left: 4px solid #3b82f6; }
				.report-stat-card.resolved { border-left: 4px solid #22c55e; }
				.report-stat-card.dismissed { border-left: 4px solid #9ca3af; }

				.report-stat-card .stat-number {
					font-size: 1.75rem;
					font-weight: 700;
					color: var(--color-gray-900);
				}

				.report-stat-card .stat-label {
					font-size: 0.75rem;
					text-transform: uppercase;
					letter-spacing: 0.05em;
					color: var(--color-gray-500);
					margin-top: 0.25rem;
				}

				.reports-list {
					display: flex;
					flex-direction: column;
					gap: 1rem;
				}

				.report-card {
					background: white;
					border: 1px solid var(--color-gray-200);
					border-radius: 0.75rem;
					padding: 1.25rem;
					border-left: 4px solid var(--color-gray-300);
				}

				.report-card.pending { border-left-color: #f59e0b; }
				.report-card.reviewed { border-left-color: #3b82f6; }
				.report-card.resolved { border-left-color: #22c55e; }
				.report-card.dismissed { border-left-color: #9ca3af; }

				.report-card-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 1rem;
				}

				.report-reason {
					display: flex;
					align-items: center;
					gap: 0.5rem;
					font-weight: 600;
					color: var(--color-gray-800);
				}

				.report-reason i {
					color: var(--color-gray-500);
				}

				.report-status {
					padding: 0.25rem 0.75rem;
					border-radius: 9999px;
					font-size: 0.75rem;
					font-weight: 600;
					text-transform: capitalize;
				}

				.report-parties {
					display: flex;
					align-items: center;
					gap: 1.5rem;
					padding: 1rem;
					background: var(--color-gray-50);
					border-radius: 0.5rem;
					margin-bottom: 1rem;
				}

				.report-party {
					flex: 1;
				}

				.report-arrow {
					color: var(--color-gray-400);
				}

				.party-label {
					font-size: 0.7rem;
					text-transform: uppercase;
					letter-spacing: 0.05em;
					color: var(--color-gray-500);
					margin-bottom: 0.25rem;
				}

				.party-name {
					font-weight: 600;
					color: var(--color-gray-900);
				}

				.party-email {
					font-size: 0.875rem;
					color: var(--color-gray-500);
				}

				.party-role {
					display: inline-block;
					margin-top: 0.25rem;
					padding: 0.125rem 0.5rem;
					background: var(--color-gray-200);
					border-radius: 9999px;
					font-size: 0.7rem;
					text-transform: capitalize;
					color: var(--color-gray-600);
				}

				.report-description {
					margin-bottom: 1rem;
				}

				.description-toggle {
					display: flex;
					align-items: center;
					gap: 0.5rem;
					background: none;
					border: none;
					color: var(--color-gray-500);
					font-size: 0.875rem;
					cursor: pointer;
					padding: 0;
				}

				.description-toggle:hover {
					color: var(--color-gray-700);
				}

				.description-content {
					margin-top: 0.75rem;
					padding: 1rem;
					background: var(--color-gray-50);
					border-radius: 0.5rem;
					font-size: 0.9375rem;
					color: var(--color-gray-700);
					line-height: 1.6;
				}

				.resolution-note {
					margin-top: 0.75rem;
					padding-top: 0.75rem;
					border-top: 1px solid var(--color-gray-200);
					font-size: 0.875rem;
				}

				.report-footer {
					display: flex;
					justify-content: space-between;
					align-items: center;
					flex-wrap: wrap;
					gap: 1rem;
				}

				.report-meta {
					display: flex;
					align-items: center;
					gap: 1rem;
					font-size: 0.875rem;
					color: var(--color-gray-500);
				}

				.report-meta i {
					margin-right: 0.375rem;
				}

				.report-chat-link {
					color: var(--color-primary);
					text-decoration: none;
				}

				.report-chat-link:hover {
					text-decoration: underline;
				}

				.report-actions {
					display: flex;
					gap: 0.5rem;
				}

				.report-actions .btn {
					display: inline-flex;
					align-items: center;
					gap: 0.375rem;
				}

				.report-actions .btn i {
					font-size: 0.75rem;
				}

				.empty-state {
					text-align: center;
					padding: 3rem;
				}

				.empty-state h3 {
					margin-bottom: 0.5rem;
					color: var(--color-gray-700);
				}

				.empty-state p {
					color: var(--color-gray-500);
				}
			`}</style>
		</div>
	);
}
